import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "wouter";
import { useMe } from "@/lib/auth";
import { pathToSection } from "@/lib/nav";
import { useToast } from "@/hooks/use-toast";
import { preloadRoute, reportRouteCacheReuse } from "@/lib/app-performance";

export interface AppTab {
  id: string;
  path: string; // app-relative, e.g. "/inventory" or "/inventory/drafts/<uuid>"
  title: string;
  draftId?: string; // optional link to a draft so we can clean it up if discarded
  pinned?: boolean; // pinned tabs (e.g. main "Inventory" list) cannot be closed
  dirty?: boolean; // form has unsaved local edits not yet auto-saved to server
  // Per-tab persisted UI state (filters, search terms, expanded rows, etc.)
  // Pages access this via the `useTabState` hook so they can survive
  // tab switches and even browser reloads. Must be JSON-serializable.
  state?: Record<string, unknown>;
}

interface TabsContextValue {
  tabs: AppTab[];
  activeId: string | null;
  // Open a tab. If a tab with the same path already exists, focus it instead.
  // Returns the active tab id, or `null` if the limit blocked the open.
  openTab: (init: Omit<AppTab, "id"> & { id?: string }) => string | null;
  focusTab: (id: string) => void;
  setTitle: (id: string, title: string) => void;
  setDirty: (id: string, dirty: boolean) => void;
  // Persist a per-tab UI value (used by `useTabState`). The updater receives
  // the previous value (or `undefined` if unset) and returns the next value.
  // No-op if the tab no longer exists.
  setTabState: <T>(
    id: string,
    key: string,
    updater: T | ((prev: T | undefined) => T),
  ) => void;
  closeTab: (id: string, opts?: { skipConfirm?: boolean }) => boolean;
  // Closes every tab except pinned ones (Dashboard). Returns the count
  // of tabs that were actually closed. Honors the dirty-confirm prompt
  // unless `skipConfirm` is set.
  closeAllTabs: (opts?: { skipConfirm?: boolean }) => number;
  anyDirty: boolean;
}

const TabsContext = createContext<TabsContextValue | null>(null);

// Bumped from v1 → v2 when the always-pinned tab was changed from Inventory
// to Dashboard. The bump prevents stale persisted state from a previous
// session keeping an old "pinned-inventory" tab around as the active tab.
const STORAGE_KEY = "eb.tabs.v2";

// Hard ceiling on the number of open tabs. Past this point the URL bar
// becomes unusable and the per-tab listeners burn measurable memory.
// When the limit is reached, we auto-evict the oldest evictable tab
// (see pickEvictionVictim) instead of blocking the open. The pinned
// dashboard tab counts toward this limit (it always exists), so the
// user can keep up to MAX_TABS - 1 ad-hoc tabs at once.
export const MAX_TABS = 12;

// Picks which tab to evict when we hit the cap. Rules:
//   1. Never evict pinned tabs (the dashboard).
//   2. Never evict the currently active tab — the user is looking at it.
//   3. Prefer the oldest CLEAN (no unsaved edits) tab so we silently
//      reclaim space without throwing away in-progress work.
//   4. If every candidate is dirty, fall back to the oldest dirty tab
//      so we still honor the cap; the caller surfaces a toast warning
//      the user that an in-progress tab was closed.
// "Oldest" == earliest in the array, since we always append on open.
function pickEvictionVictim(tabs: AppTab[], activeId: string | null): AppTab | null {
  const candidates = tabs.filter((t) => !t.pinned && t.id !== activeId);
  if (candidates.length === 0) return null;
  const clean = candidates.find((t) => !t.dirty);
  return clean ?? candidates[0];
}

// Single source of truth for the toast we surface after auto-eviction.
// Kept module-scoped so both call sites (URL-sync effect and openTab)
// stay perfectly consistent when phrasing changes.
type ToastFn = (args: {
  title: string;
  description?: string;
  variant?: "default" | "destructive";
  // Radix toast duration in ms; Infinity keeps the toast on screen until
  // the user dismisses it explicitly.
  duration?: number;
}) => unknown;
function announceEviction(toast: ToastFn, evicted: AppTab[]): void {
  if (evicted.length === 0) return;
  const dirtyCount = evicted.filter((t) => t.dirty).length;
  if (evicted.length === 1) {
    const v = evicted[0];
    toast({
      title: `Closed "${v.title}"`,
      description: v.dirty
        ? `Auto-closed to stay under the ${MAX_TABS}-tab limit. It had unsaved edits, which were discarded.`
        : `Auto-closed to stay under the ${MAX_TABS}-tab limit.`,
      variant: v.dirty ? "destructive" : "default",
      // Losing unsaved work must not scroll past unnoticed: keep the
      // notice up until the user dismisses it. Clean closes keep the
      // default auto-dismiss.
      ...(v.dirty ? { duration: Infinity } : {}),
    });
    return;
  }
  toast({
    title: `Closed ${evicted.length} tabs`,
    description:
      dirtyCount > 0
        ? `Auto-closed to stay under the ${MAX_TABS}-tab limit. ${dirtyCount} had unsaved edits, which were discarded.`
        : `Auto-closed to stay under the ${MAX_TABS}-tab limit.`,
    variant: dirtyCount > 0 ? "destructive" : "default",
    ...(dirtyCount > 0 ? { duration: Infinity } : {}),
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizePersistedTab(value: unknown): AppTab | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.id !== "string" ||
    value.id.trim().length === 0 ||
    typeof value.path !== "string" ||
    value.path.trim().length === 0 ||
    typeof value.title !== "string" ||
    value.title.trim().length === 0
  ) {
    return null;
  }

  const tab: AppTab = {
    id: value.id,
    path: value.path,
    title: value.title,
    dirty: false,
  };
  if (typeof value.draftId === "string") tab.draftId = value.draftId;
  if (value.pinned === true) tab.pinned = true;
  if (isRecord(value.state)) tab.state = value.state;
  return tab;
}

function genId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadFromStorage(): { tabs: AppTab[]; activeId: string | null } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !Array.isArray(parsed.tabs)) return null;
    // Ignore malformed entries and duplicate IDs so one corrupt local record
    // cannot break the shared shell or create ambiguous active-tab state.
    const seenIds = new Set<string>();
    let tabs: AppTab[] = [];
    for (const value of parsed.tabs) {
      const tab = normalizePersistedTab(value);
      if (!tab || seenIds.has(tab.id)) continue;
      seenIds.add(tab.id);
      tabs.push(tab);
    }
    // Dirty flags only describe in-memory unsaved state. Reconcile the active
    // ID before trimming so a stale value is never treated as protected.
    let activeId: string | null =
      typeof parsed.activeId === "string" && tabs.some((tab) => tab.id === parsed.activeId)
        ? parsed.activeId
        : tabs[0]?.id ?? null;
    // Migration: persisted state may have more tabs than the current cap
    // (we lowered MAX_TABS from 15→8). Trim from the oldest end, but always
    // preserve pinned tabs and the persisted active tab so the user lands
    // back where they were. We drop oldest first because that's the same
    // FIFO eviction order used at runtime.
    if (tabs.length > MAX_TABS) {
      const keep = new Set<string>();
      for (const t of tabs) if (t.pinned) keep.add(t.id);
      if (activeId) keep.add(activeId);
      // Walk oldest → newest dropping non-protected tabs until we fit.
      const trimmed: AppTab[] = [];
      let toDrop = tabs.length - MAX_TABS;
      for (const t of tabs) {
        if (toDrop > 0 && !keep.has(t.id)) {
          toDrop--;
          continue;
        }
        trimmed.push(t);
      }
      // If protected tabs alone still exceed the cap (pathological), drop
      // newest non-pinned protected tabs (i.e. drop the persisted active
      // marker, fall back to first tab as active).
      if (trimmed.length > MAX_TABS) {
        const overflow = trimmed.length - MAX_TABS;
        const finalTabs: AppTab[] = [];
        let dropped = 0;
        // Drop from the END (newest) so pinned dashboard at front survives.
        for (let i = trimmed.length - 1; i >= 0; i--) {
          if (dropped < overflow && !trimmed[i].pinned) {
            dropped++;
            continue;
          }
          finalTabs.unshift(trimmed[i]);
        }
        tabs = finalTabs;
      } else {
        tabs = trimmed;
      }
      // If the persisted active tab got dropped, fall back to the first tab.
      if (!tabs.some((t) => t.id === activeId)) {
        activeId = tabs[0]?.id ?? null;
      }
    }
    return { tabs, activeId };
  } catch {
    return null;
  }
}

function saveToStorage(tabs: AppTab[], activeId: string | null) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeId }));
  } catch {
    // ignore quota errors
  }
}

const PINNED_DASHBOARD_TAB: AppTab = {
  id: "pinned-dashboard",
  path: "/",
  title: "Dashboard",
  pinned: true,
};

function ensurePinned(tabs: AppTab[]): AppTab[] {
  const seen = new Set<string>();
  const deduped = tabs.filter((tab) => {
    if (seen.has(tab.id)) return false;
    seen.add(tab.id);
    return true;
  });
  const pinnedIndex = deduped.findIndex(
    (tab) => tab.pinned && tab.path === PINNED_DASHBOARD_TAB.path,
  );
  if (pinnedIndex === -1) {
    return [
      PINNED_DASHBOARD_TAB,
      ...deduped.filter((tab) => tab.id !== PINNED_DASHBOARD_TAB.id),
    ];
  }
  const [pinned] = deduped.splice(pinnedIndex, 1);
  return [pinned!, ...deduped];
}

function deriveTitle(path: string): string {
  // Best-effort fallback when a tab is auto-created on direct navigation.
  if (path === "/" || path === "") return "Dashboard";
  const seg = path.split("/").filter(Boolean);
  if (seg.length === 0) return "Dashboard";
  if (seg[0] === "inventory" && seg[1] === "drafts") return "Add Product";
  if (seg.length === 1) {
    return seg[0].charAt(0).toUpperCase() + seg[0].slice(1).replace(/-/g, " ");
  }
  return seg.map((s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, " ")).join(" · ");
}

export function TabsProvider({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { me } = useMe();
  const { toast } = useToast();
  const initialRef = useRef<{ tabs: AppTab[]; activeId: string | null } | null>(null);
  if (initialRef.current === null) {
    const restored = loadFromStorage();
    if (restored) {
      const tabs = ensurePinned(restored.tabs);
      initialRef.current = {
        tabs,
        activeId: tabs.some((tab) => tab.id === restored.activeId)
          ? restored.activeId
          : PINNED_DASHBOARD_TAB.id,
      };
    } else {
      initialRef.current = { tabs: [PINNED_DASHBOARD_TAB], activeId: PINNED_DASHBOARD_TAB.id };
    }
  }

  const [tabs, setTabs] = useState<AppTab[]>(initialRef.current.tabs);
  const [activeId, setActiveId] = useState<string | null>(initialRef.current.activeId);

  // Persist whenever tabs / activeId change.
  useEffect(() => {
    saveToStorage(tabs, activeId);
  }, [tabs, activeId]);

  // Keep the active tab in sync with the URL: when the user navigates via
  // sidebar Link / direct URL / browser back, pick the tab that matches the
  // current location, or open a fresh tab for that path if none exists. This
  // is what makes the tab system "transparent" to existing nav code.
  // When we hit MAX_TABS we auto-evict the oldest evictable tab rather than
  // blocking, so that deep-links / browser-back / sidebar nav can never
  // strand the user against the cap.
  useEffect(() => {
    const path = location || "/";
    const evictedDuringNav: AppTab[] = [];
    const committedMatch = tabs.find((tab) => tab.path === path);
    const reusedDuringNav = Boolean(committedMatch && committedMatch.id !== activeId);
    // Route chunks are fetched on direct/deep-link navigation as well as
    // sidebar intent. This is a no-op for routes that are not explicitly
    // registered (currently only the critical Inventory and Sales lists).
    void preloadRoute(path);
    setTabs((cur) => {
      const match = cur.find((t) => t.path === path);
      if (match) {
        if (match.id !== activeId) setActiveId(match.id);
        return cur;
      }
      let working = cur;
      // Loop because persisted state may already exceed the cap (user had
      // 12 tabs back when MAX_TABS was 15). One nav should not leave them
      // permanently above the limit.
      while (working.length >= MAX_TABS) {
        const victim = pickEvictionVictim(working, activeId);
        if (!victim) break;
        evictedDuringNav.push(victim);
        working = working.filter((t) => t.id !== victim.id);
      }
      if (working.length >= MAX_TABS) {
        // Nothing was evictable — bounce back to the active tab to keep
        // URL + UI in sync. (In practice unreachable: every non-pinned
        // tab would have to be the active one, which is unique.)
        const active = working.find((t) => t.id === activeId) ?? working[0];
        if (active && active.path !== path) {
          queueMicrotask(() => setLocation(active.path));
        }
        return working;
      }
      const newTab: AppTab = {
        id: genId(),
        path,
        title: deriveTitle(path),
      };
      const next = [...working, newTab];
      setActiveId(newTab.id);
      return next;
    });
    if (reusedDuringNav) {
      // This describes reuse of the mounted app tab/QueryClient entry; it
      // intentionally does not claim that a server response was a cache hit.
      reportRouteCacheReuse(path, true, "tab-revisit");
    }
    if (evictedDuringNav.length > 0) {
      announceEviction(toast, evictedDuringNav);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const openTab = useCallback<TabsContextValue["openTab"]>(
    (init) => {
      let resultId: string | null = null;
      let blocked = false;
      const reusedExisting = tabs.some((tab) => tab.path === init.path);
      const evictedDuringOpen: AppTab[] = [];
      void preloadRoute(init.path);
      setTabs((cur) => {
        // De-dupe: if a tab with this path already exists, focus it
        // instead of opening a duplicate.
        const existing = cur.find((t) => t.path === init.path);
        if (existing) {
          resultId = existing.id;
          return cur;
        }
        let working = cur;
        // Loop because persisted state may already exceed the cap.
        while (working.length >= MAX_TABS) {
          const victim = pickEvictionVictim(working, activeId);
          if (!victim) break;
          evictedDuringOpen.push(victim);
          working = working.filter((t) => t.id !== victim.id);
        }
        if (working.length >= MAX_TABS) {
          blocked = true;
          return working;
        }
        const newTab: AppTab = {
          id: init.id ?? genId(),
          path: init.path,
          title: init.title,
          draftId: init.draftId,
        };
        resultId = newTab.id;
        return [...working, newTab];
      });
      if (evictedDuringOpen.length > 0) {
        announceEviction(toast, evictedDuringOpen);
      }
      if (reusedExisting) reportRouteCacheReuse(init.path, true, "tab-revisit");
      if (blocked) {
        toast({
          title: "Maximum tab limit reached",
          description: "Please close unused tabs before opening another.",
          variant: "destructive",
        });
        return null;
      }
      // Navigate to that path; the location effect will activate the tab.
      setLocation(init.path);
      // ensure activeId is set immediately for snappy UI
      if (resultId) setActiveId(resultId);
      return resultId;
    },
    [activeId, setLocation, toast],
  );

  const focusTab = useCallback(
    (id: string) => {
      const t = tabs.find((x) => x.id === id);
      if (!t) return;
      setActiveId(id);
      if (location !== t.path) setLocation(t.path);
    },
    [tabs, location, setLocation],
  );

  // Both setters MUST be no-ops when the value is unchanged. Form pages
  // call these from useEffect on every form mutation, so producing a new
  // tabs array reference unconditionally would cause: tabs change → context
  // value changes → useOwnTab returns new object → form effect fires again
  // → setDirty called again → infinite render loop. Short-circuiting both
  // the per-tab object and the array reference breaks the cycle.
  const setTitle = useCallback((id: string, title: string) => {
    setTabs((cur) => {
      const idx = cur.findIndex((t) => t.id === id);
      if (idx === -1) return cur;
      const nextTitle = title || cur[idx].title;
      if (cur[idx].title === nextTitle) return cur;
      const next = cur.slice();
      next[idx] = { ...cur[idx], title: nextTitle };
      return next;
    });
  }, []);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    setTabs((cur) => {
      const idx = cur.findIndex((t) => t.id === id);
      if (idx === -1) return cur;
      const wasDirty = Boolean(cur[idx].dirty);
      if (wasDirty === dirty) return cur;
      const next = cur.slice();
      next[idx] = { ...cur[idx], dirty };
      return next;
    });
  }, []);

  // Persist a per-tab UI value. Backs `useTabState`. Short-circuits when
  // the resolved value is unchanged so we don't spam re-renders for
  // controlled inputs that "set" the same value on every keystroke.
  const setTabState = useCallback<TabsContextValue["setTabState"]>(
    (id, key, updater) => {
      setTabs((cur) => {
        const idx = cur.findIndex((t) => t.id === id);
        if (idx === -1) return cur;
        const prevState = (cur[idx].state ?? {}) as Record<string, unknown>;
        const prevValue = prevState[key];
        const nextValue =
          typeof updater === "function"
            ? (updater as (p: unknown) => unknown)(prevValue)
            : updater;
        if (Object.is(prevValue, nextValue)) return cur;
        const next = cur.slice();
        next[idx] = {
          ...cur[idx],
          state: { ...prevState, [key]: nextValue },
        };
        return next;
      });
    },
    [],
  );

  const closeTab = useCallback<TabsContextValue["closeTab"]>(
    (id, opts) => {
      const target = tabs.find((t) => t.id === id);
      if (!target || target.pinned) return false;
      if (target.dirty && !opts?.skipConfirm) {
        const ok = window.confirm(
          "You have unsaved changes in this tab. Close anyway and lose them?",
        );
        if (!ok) return false;
      }
      let nextActive: string | null = activeId;
      const remaining = tabs.filter((t) => t.id !== id);
      if (activeId === id) {
        const idx = tabs.findIndex((t) => t.id === id);
        const fallback = remaining[idx] ?? remaining[idx - 1] ?? remaining[0] ?? null;
        nextActive = fallback?.id ?? null;
        if (fallback) setLocation(fallback.path);
      }
      setTabs(remaining);
      setActiveId(nextActive);
      return true;
    },
    [tabs, activeId, setLocation],
  );

  const closeAllTabs = useCallback<TabsContextValue["closeAllTabs"]>(
    (opts) => {
      const closable = tabs.filter((t) => !t.pinned);
      if (closable.length === 0) return 0;
      if (!opts?.skipConfirm) {
        const dirtyCount = closable.filter((t) => t.dirty).length;
        if (dirtyCount > 0) {
          const ok = window.confirm(
            `${dirtyCount} of these tabs have unsaved changes. Close all anyway and lose them?`,
          );
          if (!ok) return 0;
        }
      }
      // Keep only pinned tabs. Activate the first remaining tab (the
      // pinned dashboard) so the user lands somewhere usable rather than
      // on a blank "no tab" state.
      const remaining = tabs.filter((t) => t.pinned);
      const fallback = remaining[0] ?? null;
      setTabs(remaining);
      setActiveId(fallback?.id ?? null);
      if (fallback && location !== fallback.path) {
        setLocation(fallback.path);
      }
      return closable.length;
    },
    [tabs, location, setLocation],
  );

  // Auto-close tabs whose section has been revoked. Runs whenever the
  // user's `sections` map changes (e.g. an admin toggled access while the
  // user was viewing this app — /api/me will refetch and update `me`).
  // Pinned tabs are never closed; the dashboard pin is always allowed.
  // The route guard (gated()) still renders <AccessDenied/> if a stale tab
  // somehow survives, so this is best-effort cleanup.
  const sections = me?.sections;
  useEffect(() => {
    if (!sections) return;
    setTabs((cur) => {
      let activeChanged = false;
      const remaining = cur.filter((t) => {
        if (t.pinned) return true;
        const sec = pathToSection(t.path);
        if (!sec) return true; // unknown path — leave it alone
        const allowed = sections[sec] === true;
        if (!allowed && t.id === activeId) activeChanged = true;
        return allowed;
      });
      if (remaining.length === cur.length) return cur;
      if (activeChanged) {
        const fallback = remaining[0] ?? null;
        if (fallback) {
          setActiveId(fallback.id);
          if (location !== fallback.path) setLocation(fallback.path);
        } else {
          setActiveId(null);
        }
      }
      return remaining;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  const anyDirty = useMemo(() => tabs.some((t) => t.dirty), [tabs]);

  const value: TabsContextValue = useMemo(
    () => ({
      tabs,
      activeId,
      openTab,
      focusTab,
      setTitle,
      setDirty,
      setTabState,
      closeTab,
      closeAllTabs,
      anyDirty,
    }),
    [
      tabs,
      activeId,
      openTab,
      focusTab,
      setTitle,
      setDirty,
      setTabState,
      closeTab,
      closeAllTabs,
      anyDirty,
    ],
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("useTabs must be used inside <TabsProvider>");
  return ctx;
}

// Hook used by form pages to register their owning tab id (so they can
// mark dirty / clean and update the tab title from the form data).
export function useOwnTab(): {
  tab: AppTab | null;
  setTitle: (title: string) => void;
  setDirty: (dirty: boolean) => void;
  close: () => void;
} {
  const { tabs, activeId, setTitle, setDirty, closeTab } = useTabs();
  const tab = tabs.find((t) => t.id === activeId) ?? null;
  return {
    tab,
    setTitle: useCallback((t: string) => tab && setTitle(tab.id, t), [tab, setTitle]),
    setDirty: useCallback((d: boolean) => tab && setDirty(tab.id, d), [tab, setDirty]),
    close: useCallback(() => tab && closeTab(tab.id, { skipConfirm: true }), [tab, closeTab]),
  };
}

// `useTabState` — drop-in replacement for `useState` that persists the
// value into the *current* tab's state bag. The value survives:
//   * Switching to another tab and back (component unmounts & remounts).
//   * A full browser reload (tab state goes through localStorage).
//
// The owning tab is identified by `path === current location` rather than
// by `activeId`, so the hook stays bound to the right tab even during the
// brief window after a route change but before the location-sync effect
// has updated `activeId`.
//
// Constraints:
//   * `defaultValue` must be JSON-serializable (no functions / class
//     instances / DOM nodes).
//   * Use stable, page-scoped keys (e.g. `"inv.searchTerm"`) so two tabs
//     viewing the same kind of page each get their own value.
//
// If no tab matches the current location (a brief window during the
// first render after a deep-link nav, before the tab record exists),
// reads return `defaultValue` and writes are no-ops. The next render
// once the tab record exists picks up the live persisted value.
export function useTabState<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const { tabs, setTabState } = useTabs();
  const [location] = useLocation();
  const tab = tabs.find((t) => t.path === location) ?? null;
  const ownerId = tab?.id ?? null;

  const stored = tab?.state?.[key];
  const value = (stored !== undefined ? stored : defaultValue) as T;

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (!ownerId) return;
      setTabState<T>(ownerId, key, (prev) => {
        const resolvedPrev = (prev !== undefined ? prev : defaultValue) as T;
        return typeof next === "function"
          ? (next as (p: T) => T)(resolvedPrev)
          : next;
      });
    },
    // defaultValue intentionally excluded — only used to seed the first
    // functional-updater call when no value has been stored yet. Including
    // it would invalidate the setter identity on every render for callers
    // that pass an inline literal/object as the default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerId, key, setTabState],
  );

  return [value, set];
}

export function UnsavedChangesGuard() {
  const { anyDirty } = useTabs();
  useEffect(() => {
    if (!anyDirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      // Modern browsers ignore the message but require returnValue to be set.
      e.returnValue = "";
      return "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyDirty]);
  return null;
}
